import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router"
import moment from "moment"
import {
  getAllConsultants,
  getAppointmentsByQuery,
  getAllCenters, getInvoiceByPatientId, updateSuggestedConsultationCharges
} from "../../core/services/api"
import {
  AppointmentStatus,
  IAppointmentDetail,
} from "../../core/services/models/AppointmentDetail"
import "./CalendarDrawer.scss"
import PermissionUtils from "../../core/store/utils/PermissionUtils"
import { MocUserRole } from "../../core/services/models/Enums"
import Button from "react-bootstrap/esm/Button"
import Modal from "react-bootstrap/Modal"
import Table from 'react-bootstrap/esm/Table'
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import dayjs from "dayjs"

const STATUS_WAITING = "Waiting";
const STATUS_ENGAGED = "Engaged";
const STATUS_DONE = "Done";

export default function CalendarDrawer() {
  const history = useHistory()
  const location = useLocation()
  const refreshAppointments = useSelector(
    (state: any) => state.drawer?.refreshAppointments
  )
  const isConsultantsViewShown = useSelector(
    (state: any) => state.drawer?.isConsultantsViewShown
  )

  const userRole = useSelector((state: any) => state?.user?.role)
  const userDetail = useSelector((state: any) => state?.user?.userDetail)

  function getAppStatus(status: any) {
    if (status === STATUS_WAITING) return AppointmentStatus.ARRIVED
    else if (status === STATUS_ENGAGED) return AppointmentStatus.ONGOING
    else if (status === STATUS_DONE) return AppointmentStatus.COMPLETED
  }

  const statuses = [STATUS_WAITING, STATUS_ENGAGED, STATUS_DONE]

  const [consultants, updateConsultants] = useState<any>()
  const [centers, updateCenters] = useState<any>()
  const [selectedTab, updateSelectedTab] = useState<any>("OPD")
  const [selectedStatus, updateSelectedStatus] = useState<any>(STATUS_WAITING)
  const [totalAppointments, updateTotalAppointments] = useState<
    Array<IAppointmentDetail>
  >()
  const [displayAppointments, updateDisplayAppointments] = useState<
    Array<IAppointmentDetail>
  >()
  const [showConsultationChargesPopUp, setShowConsultationChargesPopUp] = useState<boolean>(false)
  const [previousInvoices, setPreviousInvoices] = useState<any>([])
  const [suggestedConsultationCharges, setSuggestedConsultationCharges] = useState<any>()
  const [selectedConsultant, setSelectedConsultant] = useState<string>("")
  const [selectedAppointment, setSelectedAppointment] = useState<any>()
  const [consultationChargesError, setConsultationChargesError] = useState<string>("")

  useEffect(() => {
    if (isConsultantsViewShown !== null) {
      if (isConsultantsViewShown) {
        fetchConsultants()
        fetchAllCenters()
      } else {
        fetchAllCenters()
      }
    }
  }, [isConsultantsViewShown])

  const fetchSummaryWithQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const consultant = searchParams.get('consultant');

    let additionalQuery = ""
    if (consultant) {
      setSelectedConsultant(consultant)
      additionalQuery = `&consultantId=${consultant}`
    } else {
      setSelectedConsultant("")
    }

    fetchSummary(additionalQuery)
  }

  useEffect(() => {
    if (userRole !== MocUserRole.HOME_CARE_RMO && userRole !== MocUserRole.NURSE) {
      fetchSummaryWithQuery()
    }
  }, [location.search, refreshAppointments, isConsultantsViewShown])

  function updateSelectedConsultant(consultant: string) {
    setSelectedConsultant(consultant)
    const searchParams = new URLSearchParams(location.search);

    if (consultant) {
      searchParams.set("consultant", consultant);
    } else {
      searchParams.delete("consultant")
    }

    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }

  function fetchConsultants() {
    getAllConsultants(`/consultants?centerId=${userDetail?.currentCenter?.centerId}`)
      .then((res) => {
        updateConsultants(res)
      })
      .catch((err) => {
        console.log("Err", err)
      })
  }

  function fetchAllCenters() {
    getAllCenters()
      .then((res) => {
        if ((userRole === MocUserRole.GENETIC_COUNSELLOR ||
          userRole === MocUserRole.PSYCHOLOGIST ||
          userRole === MocUserRole.CONSULTANT) &&
          userDetail?.centers?.length > 0) {
          const temp: any = [];
          userDetail?.centers?.map((eachUserCenter: any) => {
            const find = res?.find((eachCenter: any) => eachCenter.id === eachUserCenter?.centerId);
            temp.push(find);
          })
          updateCenters(temp)
        } else {
          updateCenters(res)
        }
      })
      .catch((err) => {
        console.log("err in centers", err)
      })
  }

  function fetchSummary(additionalQuery?: string) {
    let startDate = moment().format("YYYY-MM-DD")
    let endDate = moment().format("YYYY-MM-DD")

    let query = `startDate=${startDate}&endDate=${endDate}&centerId=${userDetail?.currentCenter?.centerId}`

    if (additionalQuery) {
      query += additionalQuery
    } else {
      query =
        query +
        (!isConsultantsViewShown
          ? `&consultantId=${userDetail?.id}`
          : "")
    }

    getAppointmentsByQuery(query)
      .then((res) => {
        updateTotalAppointments(res)
      })
      .catch((err) => console.log("err", err))
  }

  useEffect(() => {
    if (selectedStatus && selectedTab) {
      const filteredAppointments = totalAppointments?.filter(
        (each) =>
          each?.appointmentType?.toLowerCase() === selectedTab?.toLowerCase() &&
          each?.status === getAppStatus(selectedStatus)
      );

      if (filteredAppointments?.length) {
        const sortByTime = (timeField: any) => (a: any, b: any) => {
          return dayjs(a[timeField], 'HH:mm:ss').diff(dayjs(b[timeField], 'HH:mm:ss'));
        };

        switch (selectedStatus) {
          case STATUS_WAITING:
            filteredAppointments.sort(sortByTime('arrivalTime'));
            break;
          case STATUS_ENGAGED:
            filteredAppointments.sort(sortByTime('engageTime'));
            break;
          case STATUS_DONE:
            filteredAppointments.sort(sortByTime('completedTime'));
            break;
        }
      }

      updateDisplayAppointments(filteredAppointments);
    }
  }, [totalAppointments, selectedStatus, selectedTab]);


  function renderTimeData(data: any) {
    const timeField = selectedStatus === STATUS_WAITING ? 'arrivalTime' :
      selectedStatus === STATUS_ENGAGED ? 'engageTime' :
        selectedStatus === STATUS_DONE ? 'completedTime' :
          null;

    if (timeField && data[timeField]) {
      return dayjs(data[timeField], "HH:mm").format("hh:mm A");
    }

    return null;
  }

  const closeConsultantChargesModal = () => {
    setShowConsultationChargesPopUp(false)
    setSuggestedConsultationCharges('')
    setPreviousInvoices([])
    setSelectedAppointment(undefined)
    setConsultationChargesError("")
  }

  const onConsultationChargesApply = () => {

    if (suggestedConsultationCharges < 0) {
      setConsultationChargesError("Consultation Charges should not be less than zero")
      return;
    }

    updateSuggestedConsultationCharges(selectedAppointment?.id, { suggestedConsultationCharges })
      .then(() => {
        closeConsultantChargesModal()
        fetchSummaryWithQuery()
      }).catch((err: any) => {
        console.log("err", err)
      })
  }

  const onChangeConsultationCharges = (appointment: any) => {
    setSelectedAppointment(appointment)
    getInvoiceByPatientId(appointment?.patientId, "OPD", 3, 0)
      .then((res) => {
        setPreviousInvoices(res);
        setShowConsultationChargesPopUp(true)
      })
      .catch((err) => {
        console.log("err", err)
      })
  }

  function renderOPDCard(data: IAppointmentDetail) {
    return (
      <div className="followup-card">
        <div className="followup-row">
          <div className="d-flex justify-content-between">
            <div className="time">{renderTimeData(data)}</div>
            <div className="patient-name">
              {PermissionUtils.displayPatientName(
                userRole,
                data,
                true,
                null,
                null
              )}
              <div className="followup-content">
                {data?.patientAppointmentType}
                <div>
                  <span>with</span> {data?.consultantName}
                </div>
              </div>
            </div>
          </div>
          <div className="status-wrapper">
            <div className={`status ${selectedStatus?.toLowerCase()}`} />
          </div>
        </div>
        {userRole === MocUserRole.CONSULTANT && data?.consultantId === userDetail?.id &&
          (
            <div className="followup-row">
              {(data?.suggestedConsultationCharges || data?.suggestedConsultationCharges === 0) ?
                <div>
                  <div>
                    Suggested charge: {data?.suggestedConsultationCharges?.toFixed(2)}
                  </div>
                  <div>
                    Paid amount: {data?.finalInvoiceAmount?.toFixed(2)}
                  </div>
                </div> : <div>
                  <Button
                    className='m-2'
                    variant='outline-success'
                    onClick={() => onChangeConsultationCharges(data)}
                  >
                    Suggest Charges
                  </Button>
                </div>}
            </div>)}
      </div>
    )
  }

  function renderIPDCard(data: IAppointmentDetail) {
    return (
      <div className="followup-ipd">
        <div className="col-12 m-0 row followup-content">
          <div className="col-3 p-0 text-center">
            <img src="../../assets/images/calendar.svg" alt="slot" />
          </div>
          <div className="col-9 p-0">
            {renderIPDContent(
              "Name",
              PermissionUtils.displayPatientName(
                userRole,
                data,
                true,
                null,
                null
              )
            )}
            {renderIPDContent("Time", `${renderTimeData(data)}`)}
            {renderIPDContent("Room", data?.allottedBed?.roomName ? data?.allottedBed?.roomName : "-")}
            {renderIPDContent("Bed", data?.allottedBed?.bedName ? data?.allottedBed?.bedName : "-")}
          </div>
        </div>
      </div>
    )
  }

  function followup(data: IAppointmentDetail) {
    return (
      <>
        {selectedTab?.toLowerCase() === "opd"
          ? renderOPDCard(data)
          : renderIPDCard(data)}
      </>
    )
  }

  function renderIPDContent(title: string, value: string) {
    return (
      <div className={`col-md-12 row ipd-content ${title?.toLowerCase()}`}>
        <div className="col-md-3 p-0 title">{title}:</div>
        <div className="col-md-9 p-0 value">
          <div className="value-text">{value}</div>
        </div>
      </div>
    )
  }

  function renderListItem(id: any, color: any, name: any) {
    return (
      <div key={id} className={`doctor-list ${id === selectedConsultant ? "active" : ""}`}>
        <div className="doctor-name-box">
          <div className="box" style={{ backgroundColor: color }} />
          <div className="doctor-name">{name}</div>
        </div>
      </div>
    )
  }

  function renderConsultantListItem(id: any, color: any, name: any) {
    return (
      <div role="button" key={id} onClick={() => {
        if (userRole !== MocUserRole.NURSE) {
          updateSelectedConsultant(id === selectedConsultant ? "" : id)
        }
      }}>
        {renderListItem(id, color, name)}
      </div>
    )
  }

  function fetchDisplayCount(type: string) {
    if (type === "OPD" || type === "IPD")
      return (
        totalAppointments?.filter(
          (each) =>
            each?.appointmentType?.toLowerCase() === type?.toLowerCase() &&
            (each?.status === AppointmentStatus.ARRIVED ||
              each?.status === AppointmentStatus.ONGOING ||
              each?.status === AppointmentStatus.COMPLETED)
        )?.length || "0"
      )
    else
      return (
        totalAppointments?.filter(
          (each) =>
            each?.appointmentType?.toLowerCase() ===
            selectedTab?.toLowerCase() && each?.status === getAppStatus(type)
        )?.length || "0"
      )
  }
  function fetchSpecialDisplayCount(type: string) {
    if (type === "SPR")
      return (
        totalAppointments?.filter(
          (each) =>
            each?.appointmentType?.toLowerCase() === type?.toLowerCase() &&
            (each?.status === AppointmentStatus.ARRIVED ||
              each?.status === AppointmentStatus.ONGOING ||
              each?.status === AppointmentStatus.COMPLETED)
        )?.length || "0"
      )
    else
      return (
        totalAppointments?.filter(
          (each) =>
            each?.appointmentType?.toLowerCase() ===
            selectedTab?.toLowerCase() && each?.status === getAppStatus(type)
        )?.length || "0"
      )
  }
  function bookAppointmentSection() {
    return (
      <div className="book-appointment-button-section">
        <div
          className="book-appointment-button cursor-pointer"
          onClick={() => history.push(`/bookAppointment`)}
        >
          <img
            src="../../assets/images/calendar.svg"
            width="30px"
            height="30px"
            alt="book"
          />
          <div>Book Appointments</div>
        </div>
      </div>
    )
  }

  function summarySection() {
    return (
      <div className="doctors-section">
        <div className="doctors">
          <div className="doctor-header">
            Summary
            <div>
              <img src="../../assets/images/bar-chart.svg" />{" "}
            </div>
          </div>
          <div className="subtext">Today</div>
          <div className="summary-tab-wrapper col-12">
            <div className="col-5">
              <div
                className={`cursor-pointer tab ${selectedTab?.toLowerCase() === "opd" ? "active" : "inActive"
                  }`}
                onClick={() => updateSelectedTab("OPD")}
              >
                <div>
                  <img src="../../assets/images/opd-icon.svg" alt="opd" className="general_reponsive_image" />
                </div>
                <div>OPD[{fetchDisplayCount("OPD")}]</div>
              </div>
            </div>

            <div className="col-md-5">
              <div
                className={`cursor-pointer tab ${selectedTab?.toLowerCase() === "ipd" ? "active" : "inActive"
                  }`}
                onClick={() => updateSelectedTab("IPD")}
              >
                <div>
                  <img src="../../assets/images/ipd_white_icon.svg" alt="ipd" className="general_reponsive_image" />
                </div>
                <div>IPD[{fetchDisplayCount("IPD")}]</div>
              </div>
            </div>
            {/* <div className="col-md-5">
              <div
                className={`tab ${selectedTab?.toLowerCase() === "spr" ? "active" : "inActive"
                  }`}
                onClick={() => updateSelectedTab("SPR")}
              >
                <div>
                  <img src="../../assets/images/ipd_white_icon.svg" alt="spr" className="general_reponsive_image" />
                </div>
                <div>SPR[{fetchDisplayCount("SPR")}]</div>
              </div>
            </div> */}
          </div>
          <div className="queue-box-wrapper col-md-12">
            {statuses?.map((eachStatus) => (
              <div key={eachStatus} className="qb-w">
                <div
                  className={`cursor-pointer queue-box ${eachStatus?.toLowerCase()} ${eachStatus === selectedStatus && "active"
                    }`}
                  onClick={() => updateSelectedStatus(eachStatus)}
                >
                  <div>
                    {eachStatus}
                    <div className="d-flex justify-content-center">
                      <div className="number-box">
                        {fetchDisplayCount(eachStatus)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {displayAppointments && displayAppointments?.length > 0 ? (
            <div className="followup-details">
              {displayAppointments?.map((appointment: any) =>
                followup(appointment)
              )}
            </div>
          ) : (
            <div className="empty-state-text"> No data available </div>
          )}
        </div>
      </div>
    )
  }

  function doctorSection() {
    return (
      <div className="doctors-section col-md-12">
        <div className="doctors">
          <div className="doctor-header">
            <div>Consultants</div>
            <div className="d-flex">
              <div>All</div>
              <img src="../../assets/images/summary.svg" alt="summary" />
            </div>
          </div>
          {consultants?.length > 0 ? (
            consultants?.map((consultant: any) =>
              renderConsultantListItem(
                consultant?.id,
                consultant?.colorCoding,
                consultant?.name
              )
            )
          ) : (
            <div className="empty-state-text">
              No consultants data available
            </div>
          )}
        </div>
      </div>
    )
  }

  function centersListSection() {
    return (
      <div className="doctors-section col-md-12">
        <div className="doctors">
          <div className="doctor-header">
            <div>Location</div>
            <div>
              <img src="../../assets/images/Location.svg" alt="location" />
            </div>
          </div>
          {centers?.length > 0 ? (
            centers?.map((center: any) =>
              renderListItem(center?.id, center?.colorCode, center?.centerName)
            )
          ) : (
            <div className="empty-state-text">No centers data available</div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="calendar-drawer-wrapper">
      <Modal size="lg" show={showConsultationChargesPopUp} onHide={closeConsultantChargesModal}>
        <Modal.Header closeButton>
          <h5>Consultation Charges</h5>
        </Modal.Header>
        <Modal.Body>
          <h5>Previous Invoices</h5>
          {previousInvoices?.length > 0 ?
            <>
              {
                previousInvoices.map((invoice: any, invoiceIndex: any) => {
                  return (
                    <Accordion className="mb-2 rounded">
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                      >
                        <div className="d-flex w-100 px-5 justify-content-between align-items-center">
                          <div style={{ width: "100px" }}>&nbsp;
                            {moment(invoice?.appointmentDate
                            ).format("DD-MM-YYYY")}
                          </div>
                          <div style={{ width: "50px" }}>{invoice?.appointmentType}</div>
                          <div style={{ width: "100px" }}>
                            <div>Total</div>
                            <div>{invoice?.finalAmount?.toFixed(2)}</div>
                          </div>

                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Table bordered>
                          <thead>
                            <tr>
                              <th>Treatment and Details</th>
                              <th>Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoice?.invoiceItems.map((item: any, index: any) => (
                              <tr
                                key={`invoice-${invoiceIndex}-item-${index}`}
                              >
                                <td>{item.treatmentAndDetails}</td>
                                <td>{item.charges.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  )
                })}
            </>
            : <div>No previous invoices found</div>}
          <div className='mt-5 d-flex align-items-center'>
            <h5 className="me-3">Suggested Consultation Charges</h5>
            <input
              type="number"
              className="form-control w-25"
              id="suggested-consultation-charges"
              name="Suggested Charges"
              value={suggestedConsultationCharges}
              onChange={(e: any) => {
                setConsultationChargesError('')
                setSuggestedConsultationCharges(e.target.value)
              }}
            />
          </div>
          {consultationChargesError && <div className="text-danger">{consultationChargesError}</div>}
        </Modal.Body>
        <Modal.Footer>
          {suggestedConsultationCharges && <Button variant='primary' onClick={onConsultationChargesApply}>Apply Suggested Price</Button>}
          <Button variant='outline-secondary' onClick={closeConsultantChargesModal}>Close</Button>
        </Modal.Footer>
      </Modal >
      {(userRole === MocUserRole.FRONT_DESK ||
        userRole === MocUserRole.RMO || userRole === MocUserRole.HOME_CARE_NURSE || userRole === MocUserRole.HOME_CARE_RMO) &&
        bookAppointmentSection()}
      {isConsultantsViewShown ? doctorSection() : null}
      {userRole !== MocUserRole.HOME_CARE_RMO && userRole !== MocUserRole.NURSE && summarySection()}
      {userRole !== MocUserRole.HOME_CARE_RMO && userRole !== MocUserRole.HOME_CARE_NURSE ? centersListSection() : null}
    </div>
  )
}
